import React from "react";
import { Stethoscope, Smartphone, Wallet, ExternalLink, BadgeCheck } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { trackAndOpen } from "./ProductsSection";

const ICONS = { stethoscope: Stethoscope, smartphone: Smartphone, wallet: Wallet };

export const AnalyzersSection = ({ section, products }) => {
    const analyzers = products.filter((p) => p.category === "analyzer");
    return (
        <section id="analyzers" className="py-16 sm:py-20">
            <SectionHeading eyebrow="Measure it" title={section.title} intro={section.intro} />

            <Reveal className="mt-12">
                <div className="overflow-x-auto rounded-xl border border-[#E4DFD3]">
                    <table data-testid="analyzer-comparison-table" className="w-full min-w-[520px] text-left text-sm">
                        <thead>
                            <tr className="bg-[#222522] text-[#FDFCF8]">
                                {section.table.headers.map((h) => (
                                    <th key={h} className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4DFD3] bg-white">
                            {section.table.rows.map((row, i) => (
                                <tr key={i} className={row.highlighted ? "bg-[#DDE5DB]/60" : ""}>
                                    {row.cells.map((c, j) => (
                                        <td key={j} className={`px-4 py-3.5 ${j === 0 ? "font-semibold text-[#1A1D1A]" : "text-[#6D736D]"}`}>
                                            <span className="flex items-center gap-2">
                                                {c}
                                                {j === 0 && row.highlighted && <BadgeCheck size={15} strokeWidth={1.8} className="text-[#576856]" />}
                                            </span>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Reveal>

            <div className="mt-10 grid gap-6 sm:grid-cols-3">
                {analyzers.map((p, i) => {
                    const Icon = ICONS[p.icon] || Smartphone;
                    return (
                        <Reveal key={p.id} delay={i * 0.1} className="h-full">
                            <article
                                data-testid={`analyzer-card-${p.id}`}
                                className={`flex h-full flex-col rounded-[20px] border bg-white p-6 transition-shadow duration-500 hover:shadow-[0_20px_50px_-20px_rgba(34,37,34,0.25)] ${
                                    p.highlighted ? "border-[#576856] shadow-[0_16px_40px_-20px_rgba(87,104,86,0.45)]" : "border-[#E4DFD3]"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${p.highlighted ? "bg-[#576856] text-white" : "bg-[#DDE5DB] text-[#576856]"}`}>
                                        <Icon size={20} strokeWidth={1.6} />
                                    </span>
                                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                                        p.highlighted ? "bg-[#576856] text-white" : "bg-[#F5F2EA] text-[#6D736D]"
                                    }`}>
                                        {p.badge}
                                    </span>
                                </div>
                                <h3 className="mt-5 font-serif text-lg font-semibold text-[#1A1D1A]">{p.name}</h3>
                                <p className="mt-1 font-serif italic text-sm text-[#718370]">{p.tagline}</p>
                                <p className="mt-3 flex-1 text-xs sm:text-sm leading-relaxed text-[#6D736D]">{p.description}</p>
                                {p.has_button && (
                                    <div className="mt-5">
                                        <a
                                            href={p.href}
                                            target="_blank"
                                            rel="nofollow sponsored noopener"
                                            data-testid={`amazon-buy-btn-${p.id}`}
                                            onClick={(e) => { e.preventDefault(); trackAndOpen(p); }}
                                            className={`flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-300 active:scale-[0.98] ${
                                                p.highlighted
                                                    ? "bg-[#576856] text-white hover:bg-[#1A1D1A]"
                                                    : "border border-[#C4BCA8] text-[#576856] hover:border-[#576856] hover:bg-[#F5F2EA]"
                                            }`}
                                        >
                                            View on Amazon
                                            <ExternalLink size={14} strokeWidth={1.8} />
                                        </a>
                                        <p className="mt-2 text-center text-[10px] uppercase tracking-wider text-[#6D736D]/70">Opens Amazon · affiliate link</p>
                                    </div>
                                )}
                            </article>
                        </Reveal>
                    );
                })}
            </div>
        </section>
    );
};
